import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import type { MembershipPayload, TeamPayload } from '@/store/types'
import { useWorkviewTrackerStore } from '@/modules/Workview/useWorkviewTrackerStore'
import { getRelevantTeamMemberships } from '@/modules/Teams/getRelevantTeamMemberships'
import { useMembershipStore } from '@/modules/Teams/useMembershipStore'
import { updateTeam as doUpdateTeam } from '@/modules/Teams/updateTeam'
import { updateTeamAvatar as doUpdateTeamAvatar } from '@/modules/Teams/updateTeamAvatar'
import { deleteTeam as doDeleteTeam } from '@/modules/Teams/deleteTeam'
import { leaveTeam as doLeaveTeam } from '@/modules/Teams/leaveTeam'
import { registerTeam as doRegisterTeam } from '@/modules/Teams/registerTeam'
import type { UpdateTeamPayload } from '@/modules/Teams/types'
import type { ParsedError } from '@/backend/error/types'

// TODO: implement proper solution with a backend endpoint
const Watermarks = {
  'staging.v7labs.com': new Map([
    [1, 'V7 Labs'],
    [344, 'V7 Labs'],
  ]),
  'darwin.v7labs.com': new Map([
    [1, 'V7 Labs'],
    [18440, 'Bdeo Technologies S.L. - Confidential Media'],
  ]),
}

const getWatermark = (hostname: keyof typeof Watermarks, teamId: number): string | null =>
  Watermarks[hostname as keyof typeof Watermarks]?.get(teamId) ?? null

/**
 * The main store for all team related data, to be used with any new code.
 */
export const useTeamStore = defineStore('team', () => {
  const workviewTracker = useWorkviewTrackerStore()
  const membershipStore = useMembershipStore()

  const teams = ref<TeamPayload[]>([])
  const pushTeam = (team: TeamPayload): void => {
    const index = teams.value.findIndex((t) => t.id === team.id)
    if (index > -1) {
      teams.value.splice(index, 1, team)
    } else {
      teams.value.push(team)
    }
  }

  const setTeams = (newTeams: TeamPayload[]): void => {
    teams.value = newTeams
  }

  const currentTeam = ref<TeamPayload | null>(null)
  const setCurrentTeam = (team: TeamPayload | null): void => {
    currentTeam.value = team
    workviewTracker.setWorklogVersion(currentTeam.value?.work_log_version)
  }

  const setCurrentTeamAvatarUrl = (url: string): void => {
    if (!currentTeam.value) {
      return
    }
    if (!currentTeam.value.image) {
      return
    }
    currentTeam.value.image.url = url
  }

  const currentTeamWatermark = ref<string | null>(null)
  const setCurrentTeamWatermark = (watermark: string | null): void => {
    currentTeamWatermark.value = watermark
  }

  const updateTeam = async (
    teamId: number,
    updatePayload: UpdateTeamPayload,
  ): ReturnType<typeof doUpdateTeam> => {
    const response = await doUpdateTeam(teamId, updatePayload)

    if (response.ok) {
      setCurrentTeam(response.data)
      pushTeam(response.data)
    }

    return response
  }

  const updateTeamAvatar = async (
    team: TeamPayload,
    image: Blob,
    type: string,
  ): Promise<void | ParsedError> => {
    if (!team.image?.upload_url) {
      return
    }
    const response = await doUpdateTeamAvatar(team.id, team.image.upload_url, image, type)
    if (!response) {
      setCurrentTeamAvatarUrl(team.image.url)
    }

    return response
  }

  const registerTeam = async (params: {
    name: string
    invitationToken: string
    avatar: {
      hash: string
      file: Blob
      type: string
    } | null
  }): ReturnType<typeof doRegisterTeam> => {
    const response = await doRegisterTeam({
      name: params.name,
      avatarHash: params.avatar ? params.avatar.hash : null,
      invitationToken: params.invitationToken,
    })

    if (!response.ok) {
      return response
    }

    setCurrentTeam(response.data)
    pushTeam(response.data)

    if (params.avatar && response.data.image?.upload_url) {
      await updateTeamAvatar(response.data, params.avatar.file, params.avatar.type)
    }

    return response
  }

  const leaveTeam = (membershipId: number): ReturnType<typeof doLeaveTeam> =>
    doLeaveTeam(membershipId)

  const deleteTeam = (teamId: number): ReturnType<typeof doDeleteTeam> => doDeleteTeam(teamId)

  const currentTeamIsClient = computed(() => currentTeam.value?.managed_status === 'client')

  /**
   * Retrieves the partner name for client teams
   */
  const partnerTeamName = computed(() => {
    if (!currentTeamIsClient.value) {
      return null
    }

    if (currentTeam.value === null || !currentTeam.value.partner) {
      console.warn('Trying to render client UI for non-client team')
      return
    }

    return currentTeam.value.partner.name
  })

  const allRelevantMemberships = computed(() =>
    currentTeam.value
      ? getRelevantTeamMemberships(currentTeam.value, membershipStore.memberships)
      : [],
  )

  const currentTeamMemberships = computed(() =>
    allRelevantMemberships.value.filter((m) => m.team_id === currentTeam.value?.id),
  )

  const membershipInRelevantTeamsByUserId = (userId: number): MembershipPayload | null => {
    const relevantTeamIds: number[] = []

    if (currentTeam.value) {
      relevantTeamIds.push(currentTeam.value.id)
    }

    if (currentTeam.value?.managed_status === 'client' && currentTeam.value.partner_id) {
      relevantTeamIds.push(currentTeam.value.partner_id)
    }

    if (currentTeam.value?.managed_status === 'partner' && currentTeam.value.clients) {
      relevantTeamIds.concat(currentTeam.value.clients.map((t) => t.id))
    }

    return (
      membershipStore.memberships.find(
        (m) => relevantTeamIds.includes(m.team_id) && m.user_id === userId,
      ) || null
    )
  }

  watch(
    () => [
      currentTeam.value?.id,
      currentTeam.value?.partner_id,
      membershipStore.memberships.length,
    ],
    (newValues, oldValues) => {
      if (oldValues) {
        const [newTeamId, newPartnerId, newMembershipsLength] = newValues
        const [oldTeamId, oldPartnerId, oldMembershipsLength] = oldValues

        // avoid unnecessary calcs
        if (
          newTeamId === oldTeamId &&
          newPartnerId === oldPartnerId &&
          newMembershipsLength === oldMembershipsLength
        ) {
          return
        }
      }

      // This is an unfortunate check we do to add a watermark only for one client (and for test)
      const teamId = currentTeam.value?.partner_id || currentTeam.value?.id
      if (teamId) {
        const hostname = window.location.hostname as keyof typeof Watermarks
        setCurrentTeamWatermark(getWatermark(hostname, teamId))
      }

      membershipStore.syncMembershipMaps()
    },
    { immediate: true },
  )

  const reset = (): void => {
    teams.value = []
    currentTeam.value = null
  }

  return {
    teams,
    pushTeam,
    setTeams,

    currentTeam,
    partnerTeamName,
    setCurrentTeam,
    setCurrentTeamAvatarUrl,
    currentTeamWatermark,
    setCurrentTeamWatermark,

    registerTeam,
    updateTeam,
    updateTeamAvatar,
    leaveTeam,
    deleteTeam,

    currentTeamIsClient,
    membershipInRelevantTeamsByUserId,

    // memberships
    currentTeamMemberships,
    allRelevantMemberships,

    reset,
  }
})
